/**
 * Story 054 — Add-site modal (`/sites?new=1`).
 *
 * Only `siteName` is user-required; everything else is optional. The BE
 * generates `id`, `siteCode`, `created*`, `updated*`. The FE never
 * sends `id` / `siteCode` / audit columns — request body is built from
 * a Zod-validated subset.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { useToast } from "../../account/ToastContext.js";
import {
  useCreateSiteMutation,
  type CreateSiteBody,
  type ExternalId,
} from "../queries/index.js";
import { CountryAutocomplete } from "../shared/CountryAutocomplete.js";
import { ExternalIdsField } from "../shared/ExternalIdsField.js";
import { toAlpha2 } from "../shared/iso3166.js";

interface AddSiteFormValues {
  siteName: string;
  countryCode: string | null;
  latitude: string;
  longitude: string;
  isOffshore: boolean;
  aviationAuthority: string;
  controlCenterPhoneNumber: string;
  additionalDescription: string;
  externalIds: ExternalId[];
}

const EMPTY: AddSiteFormValues = {
  siteName: "",
  countryCode: null,
  latitude: "",
  longitude: "",
  isOffshore: false,
  aviationAuthority: "",
  controlCenterPhoneNumber: "",
  additionalDescription: "",
  externalIds: [],
};

function toNumberOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function dropEmptyExternalIds(arr: ExternalId[]): ExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildCreateBody(v: AddSiteFormValues): CreateSiteBody {
  // `assets/v1@4.2.0 CreateSiteBody.required: [siteName, isOffshore]`.
  // `isOffshore` MUST be on every POST -- `false` is a legitimate
  // explicit choice, not an "omit me" signal (R58-W-02 bug A).
  const body: CreateSiteBody = {
    siteName: v.siteName.trim(),
    isOffshore: v.isOffshore,
  };
  if (v.countryCode) {
    // R58-W-02 bug B -- contract narrowed countryCode to alpha-2 under
    // FEAT-010 / 2.0.0. `toAlpha2` is a passthrough for already-alpha-2
    // values (new default) and a lookup for any stray alpha-3 input.
    body.countryCode = toAlpha2(v.countryCode);
  }
  const lat = toNumberOrNull(v.latitude);
  if (lat !== null) body.latitude = lat;
  const lon = toNumberOrNull(v.longitude);
  if (lon !== null) body.longitude = lon;
  if (v.aviationAuthority.trim().length > 0) {
    body.aviationAuthority = v.aviationAuthority.trim();
  }
  if (v.controlCenterPhoneNumber.trim().length > 0) {
    body.controlCenterPhoneNumber = v.controlCenterPhoneNumber.trim();
  }
  if (v.additionalDescription.trim().length > 0) {
    body.additionalDescription = v.additionalDescription;
  }
  const ids = dropEmptyExternalIds(v.externalIds);
  if (ids.length > 0) body.externalIds = ids;
  return body;
}

export interface AddSiteDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddSiteDialog({
  open,
  onClose,
}: AddSiteDialogProps): ReactElement {
  const { toast } = useToast();
  const create = useCreateSiteMutation();
  // SB2-CC-04 — defence-in-depth: if the page-level Add-button gate is
  // bypassed (deep-link, future regression), the modal renders a
  // banner explaining why and suppresses the Save button.
  const canCreate = useCanEditResourceFor("site.create");
  const { control, register, handleSubmit, reset, watch, formState } =
    useForm<AddSiteFormValues>({
      defaultValues: EMPTY,
      mode: "onChange",
    });
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset the form whenever the dialog opens/closes.
  useEffect(() => {
    if (open) {
      reset(EMPTY);
      setSubmitError(null);
    }
  }, [open, reset]);

  const siteName = watch("siteName");
  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const canSave = siteName.trim().length > 0 && !create.isPending;

  async function onSubmit(values: AddSiteFormValues): Promise<void> {
    setSubmitError(null);
    try {
      const created = await create.mutateAsync(buildCreateBody(values));
      toast(`Site added (code ${String(created.siteCode)}).`);
      onClose();
    } catch (err) {
      // R58-W-02: surface the BE error envelope verbatim where useful so
      // the user sees WHY the save failed (validation key, RBAC reason)
      // instead of a generic "unknown error". The envelope mirrors the
      // assets/v1@4.2.0 ApiError schema -- `{ code, detail?, reason? }`.
      const errStatus = (err as { status?: number }).status;
      const errBody = (
        err as {
          body?: { code?: string; detail?: string; reason?: string };
        }
      ).body;
      const code = errBody?.code;
      const detail = errBody?.detail;
      const reason = errBody?.reason;
      let toastMessage: string;
      if (detail?.startsWith("forbidden_field")) {
        toastMessage = "Cannot set this field — it's managed by the system.";
      } else if (errStatus === 403 || code === "forbidden") {
        toastMessage = "You don't have permission to perform this action.";
      } else if (detail) {
        // AC2 -- Server-side validation toast names the cause.
        toastMessage = `Couldn't save site — ${detail}`;
      } else if (reason) {
        toastMessage = `Couldn't save site — ${reason}`;
      } else if (code) {
        toastMessage = `Couldn't save site — ${code}`;
      } else {
        toastMessage = "Couldn't add site. Try again.";
      }
      toast(toastMessage, "error");
      setSubmitError(detail ?? reason ?? code ?? "Unknown error");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-site-heading"
    >
      <DialogTitle id="add-site-heading">Add site</DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          {!canCreate ? (
            <Alert
              severity="info"
              variant="outlined"
              role="status"
              aria-live="polite"
              data-testid="add-modal-permission-banner"
              sx={{ mb: 2 }}
            >
              Your role does not allow adding a site.
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <TextField
              {...register("siteName", { required: true, minLength: 1 })}
              label="Site name"
              required
              // Modals legitimately autofocus their first editable
              // field — the user's intent is "I want to add a site"
              // and the dialog's first stop should be the only
              // required field. Story 052 / 053 modals do the same.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              slotProps={{
                htmlInput: {
                  "aria-label": "Site name",
                  "aria-required": true,
                  maxLength: 255,
                },
              }}
              fullWidth
            />
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <CountryAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  latitude={toNumberOrNull(latitude)}
                  longitude={toNumberOrNull(longitude)}
                />
              )}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("latitude")}
                label="Latitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Latitude",
                    step: "any",
                    min: -90,
                    max: 90,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("longitude")}
                label="Longitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Longitude",
                    step: "any",
                    min: -180,
                    max: 180,
                  },
                }}
                fullWidth
              />
            </Stack>
            <Controller
              name="isOffshore"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  value={field.value ? "offshore" : "onshore"}
                  onChange={(_e, next) => {
                    if (next === null) return;
                    field.onChange(next === "offshore");
                  }}
                  size="small"
                  aria-label="On/Offshore"
                >
                  <ToggleButton value="onshore">Onshore</ToggleButton>
                  <ToggleButton value="offshore">Offshore</ToggleButton>
                </ToggleButtonGroup>
              )}
            />
            <TextField
              {...register("aviationAuthority")}
              label="Aviation authority"
              slotProps={{
                htmlInput: {
                  "aria-label": "Aviation authority",
                  maxLength: 255,
                },
              }}
              fullWidth
            />
            <TextField
              {...register("controlCenterPhoneNumber")}
              label="Control centre phone"
              slotProps={{
                htmlInput: {
                  "aria-label": "Control centre phone",
                  maxLength: 50,
                },
              }}
              fullWidth
            />
            <TextField
              {...register("additionalDescription")}
              label="Additional description"
              multiline
              rows={3}
              slotProps={{
                htmlInput: { "aria-label": "Additional description" },
              }}
              fullWidth
            />
            <ExternalIdsField control={control} register={register} />
            {submitError ? (
              <Box
                role="alert"
                sx={{
                  color: "error.main",
                  fontSize: 13,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,69,58,0.10)"
                      : "rgba(255,59,48,0.06)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                {submitError}
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          {canCreate ? (
            <Button
              type="submit"
              variant="contained"
              disabled={!canSave || formState.isSubmitting}
              startIcon={
                create.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              Save
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
